import React, { useState, useEffect } from "react";
import {
  RoleDistributorState,
  RoleDistributorAction,
} from "./RoleDistributorReducer";
import { RoleDistributorSocketService } from "./RoleDistributorSocketService";
import { Room } from "../../module/types";
import { useTranslation } from "react-i18next";
import { getCoreActions } from "./abilities";
import { GAME_PRESETS, CharacterPreset } from "../../module/gamePresets";

interface Props {
  state: RoleDistributorState;
  dispatch: React.Dispatch<RoleDistributorAction>;
  room: Room;
  socketService: RoleDistributorSocketService | null;
  validationError?: string;
  setValidationError?: React.Dispatch<React.SetStateAction<string>>;
}

const MAX_LABEL_LENGTH = 10;

const RoleDistributorInGameSetting: React.FC<Props> = ({
  state,
  dispatch,
  room,
  socketService,
  validationError = "",
  setValidationError,
}) => {
  const { t, i18n } = useTranslation();
  const [labelName, setLabelName] = useState("");
  const [labelCount, setLabelCount] = useState(1);
  const [isEditingCount, setIsEditingCount] = useState(false);
  const [selectedAbilities, setSelectedAbilities] = useState<string[]>([]); // 새 역할의 선택된 액션들
  const [defaultLabel, setDefaultLabel] = useState(
    state.defaultLabel?.name || ""
  );
  const [error, setError] = useState("");

  // Preset-related state
  const [selectedGame, setSelectedGame] = useState<string>("");
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [tempSelectedCharacter, setTempSelectedCharacter] = useState<CharacterPreset | null>(null);

  // 에러 설정 함수 (외부 validationError가 있으면 그것을 우선 사용)
  const setErrorMessage = (message: string) => {
    if (setValidationError) {
      setValidationError(message);
    } else {
      setError(message);
    }
  };

  // 현재 에러 메시지 (외부 validationError가 있으면 그것을 우선 사용)
  const currentError = validationError || error;

  // state.defaultLabel이 변경될 때 로컬 state 동기화
  useEffect(() => {
    setDefaultLabel(state.defaultLabel?.name || "");
  }, [state.defaultLabel?.name]);

  // 설정 완료 상태가 변경될 때 에러 메시지 지우기
  useEffect(() => {
    if (state.settingsCompleted) {
      setErrorMessage("");
    }
  }, [state.settingsCompleted]);

  // ==================== EVENT HANDLERS ====================

  // 역할 추가
  const handleAddLabel = () => {
    if (!labelName.trim()) {
      setErrorMessage(t("RoleDistributor.enterRoleName"));
      return;
    }
    if (labelName.length > MAX_LABEL_LENGTH) {
      setErrorMessage(
        t("RoleDistributor.roleNameTooLong", { maxLength: MAX_LABEL_LENGTH })
      );
      return;
    }
    if (state.labels.some((l) => l.name === labelName)) {
      setErrorMessage(t("RoleDistributor.roleNameExists"));
      return;
    }
    if (labelCount < 1) {
      setErrorMessage(t("RoleDistributor.quantityMinimum"));
      return;
    }
    dispatch({
      type: "ADD_LABEL",
      label: {
        name: labelName,
        count: labelCount,
        explain: "",
        abilities: selectedAbilities,
      },
    });
    setLabelName("");
    setLabelCount(1);
    setSelectedAbilities([]); // 선택된 액션 초기화
    setErrorMessage("");
  };

  // 역할 삭제
  const handleRemoveLabel = (name: string) => {
    dispatch({ type: "REMOVE_LABEL", name });
    // 특수 역할 삭제 시 에러 클리어 (인원수 초과 에러가 해결될 수 있음)
    setErrorMessage("");
  };

  // default 역할 변경
  const handleDefaultLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDefaultLabel(e.target.value);
    dispatch({
      type: "SET_DEFAULT_LABEL",
      defaultLabel: { 
        name: e.target.value, 
        explain: state.defaultLabel?.explain || "",
        abilities: state.defaultLabel?.abilities || []
      },
    });
    // 사용자가 기본 역할을 입력하면 에러 클리어
    if (e.target.value.trim()) {
      setErrorMessage("");
    }
  };

  // 호스트 공개 모드 스위치
  const handleHostRevealChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_HOST_REVEAL", value: e.target.checked });
    // 설정 변경 시 에러 클리어
    setErrorMessage("");
  };

  // 새 역할의 액션 선택 토글 핸들러
  const handleNewRoleAbilityToggle = (abilityId: string) => {
    setSelectedAbilities(prev => {
      if (prev.includes(abilityId)) {
        return prev.filter(id => id !== abilityId);
      } else {
        return [...prev, abilityId];
      }
    });
  };

  // 기본 역할의 액션 토글 핸들러 (기본 역할은 여전히 편집 가능)
  const handleDefaultAbilityToggle = (abilityId: string) => {
    const currentAbilities = state.defaultLabel?.abilities || [];
    const newAbilities = currentAbilities.includes(abilityId)
      ? currentAbilities.filter(id => id !== abilityId)
      : [...currentAbilities, abilityId];

    dispatch({
      type: "SET_ROLE_ABILITIES",
      roleName: "", // 기본 역할은 이름이 없음
      abilities: newAbilities,
      isDefault: true
    });
  };

  // 게임 선택 시 모달만 열기 (기본 역할 자동 적용 제거)
  const handleGameSelect = (gameId: string) => {
    setSelectedGame(gameId);
    // 모달만 열기
    setIsPresetModalOpen(true);
    setTempSelectedCharacter(null);
  };

  // 모달에서 캐릭터 임시 선택
  const handleTempSelectCharacter = (character: CharacterPreset) => {
    setTempSelectedCharacter(character);
  };

  // 모달에서 선택 확정
  const handleConfirmPreset = () => {
    if (tempSelectedCharacter) {
      const game = GAME_PRESETS.find(g => g.id === selectedGame);
      const isDefaultRole = game?.defaultRole.id === tempSelectedCharacter.id;

      const localizedName = i18n.language === "ko"
        ? tempSelectedCharacter.name_ko
        : tempSelectedCharacter.name_en;

      if (isDefaultRole) {
        // 기본 역할로 적용
        setDefaultLabel(localizedName);
        dispatch({
          type: "SET_DEFAULT_LABEL",
          defaultLabel: {
            name: localizedName,
            explain: "",
            abilities: tempSelectedCharacter.defaultAbilities,
          },
        });
      } else {
        // 특수 역할로 적용 (기존 동작)
        setLabelName(localizedName);
        setSelectedAbilities(tempSelectedCharacter.defaultAbilities);
      }
    }
    setIsPresetModalOpen(false);
    setTempSelectedCharacter(null);
    setSelectedGame(""); // 버튼 unselected
  };

  // 모달 닫기
  const handleCancelPreset = () => {
    setIsPresetModalOpen(false);
    setTempSelectedCharacter(null);
  };

  // // 설정 완료 버튼 클릭 시
  // const handleCompleteSettings = async () => {
  //   // 1. 기본 역할 검증 (공백 제거 후 확인)
  //   if (!state.defaultLabel?.name || !state.defaultLabel.name.trim()) {
  //     setErrorMessage(t("RoleDistributor.enterBasicRole"));
  //     return;
  //   }

  //   // 2. 참여 인원 검증
  //   if (state.players.length === 0) {
  //     setErrorMessage(t("RoleDistributor.noParticipants"));
  //     return;
  //   }

  //   // 3. 특수역할 인원 수 총합 검증 (사회자 모드일 때 방장 제외)
  //   const totalLabelCount = state.labels.reduce((sum, l) => sum + l.count, 0);
  //   const eligiblePlayerCount = state.hostReveal
  //     ? state.players.length - 1
  //     : state.players.length;
  //   if (totalLabelCount > eligiblePlayerCount) {
  //     setErrorMessage(
  //       t("RoleDistributor.specialRolesExceedPlayers", {
  //         count: eligiblePlayerCount,
  //       })
  //     );
  //     return;
  //   }

  //   // 4. 소켓 서비스 확인
  //   if (!socketService) {
  //     setErrorMessage(t("RoleDistributor.serverDisconnected"));
  //     return;
  //   }


  //   // 서버에 설정 완료 요청
  //   try {
  //     socketService.completeSettings(
  //       room.roomName,
  //       state.defaultLabel,
  //       state.labels,
  //       state.hostReveal
  //     );
  //     setErrorMessage("");
  //   } catch (error) {
  //     setErrorMessage(t("RoleDistributor.settingsCompleteFailed"));
  //   }
  // };

  // // 분배 시작 버튼 클릭 시
  // const handleStart = async () => {
  //   if (!socketService) {
  //     setErrorMessage(t("RoleDistributor.serverDisconnected"));
  //     return;
  //   }

  //   // 서버에 역할 룰렛 시작 요청
  //   try {
  //     socketService.startDistribution(
  //       room.roomName,
  //       state.defaultLabel,
  //       state.labels
  //     );
  //     setErrorMessage("");
  //   } catch (error) {
  //     setErrorMessage(t("RoleDistributor.settingsCompleteFailed"));
  //   }
  // };

  // // 설정 수정하기 버튼 클릭 시
  // const handleResetSettings = async () => {
  //   if (!socketService) {
  //     setErrorMessage(t("RoleDistributor.serverDisconnected"));
  //     return;
  //   }

  //   try {
  //     socketService.resetSettings(room.roomName);
  //     setErrorMessage("");
  //   } catch (error) {
  //     setErrorMessage(t("RoleDistributor.settingsCompleteFailed"));
  //   }
  // };

  // // 종료 버튼
  // const handleEnd = () => {
  //   if (!socketService) {
  //     setErrorMessage(t("RoleDistributor.serverDisconnected"));
  //     return;
  //   }

  //   try {
  //     socketService.endDistribution(room.roomName);
  //     setErrorMessage("");
  //   } catch (error) {
  //     setErrorMessage(t("RoleDistributor.settingsCompleteFailed"));
  //   }
  // };

  // ==================== RENDER FUNCTIONS ====================

  // 프리셋 선택기 렌더링 (게임 버튼만 표시, 클릭 시 모달 열기)
  const renderPresetSelector = () => {
    const coreActions = getCoreActions(t);

    return (
      <>
        {/* 게임 선택 버튼 영역 */}
        <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-700/30 rounded-lg px-3 py-2 mb-2">
          <div className="text-sm font-medium text-indigo-300 mb-2 flex items-center">
            <i className="fas fa-magic mr-2" />
            {t("RoleDistributor.presetTitle")}
          </div>

          {/* Game Category Buttons */}
          <div className="flex gap-2">
            {GAME_PRESETS.map(game => (
              <button
                key={game.id}
                onClick={() => handleGameSelect(game.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedGame === game.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                }`}
              >
                {i18n.language === "ko" ? game.name_ko : game.name_en}
              </button>
            ))}
          </div>
        </div>

        {/* 프리셋 모달 */}
        {isPresetModalOpen && selectedGame && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-indigo-700/50 rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
              {/* 모달 헤더 */}
              <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 px-4 py-3 border-b border-indigo-700/30">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <i className="fas fa-users-cog mr-2 text-indigo-400" />
                  {i18n.language === "ko"
                    ? GAME_PRESETS.find(g => g.id === selectedGame)?.name_ko
                    : GAME_PRESETS.find(g => g.id === selectedGame)?.name_en}
                  {" "}{t("RoleDistributor.selectCharacter")}
                </h3>
              </div>

              {/* 모달 컨텐츠 - 캐릭터 그리드 */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* 기본 역할 섹션 */}
                <div className="mb-4">
                  <div className="text-sm font-medium text-blue-300 mb-2 flex items-center">
                    <i className="fas fa-user mr-2" />
                    {t("RoleDistributor.defaultRoleSection", "기본 역할")}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(() => {
                      const game = GAME_PRESETS.find(g => g.id === selectedGame);
                      const defaultRole = game?.defaultRole;
                      if (!defaultRole) return null;

                      const isSelected = tempSelectedCharacter?.id === defaultRole.id;
                      return (
                        <button
                          key={defaultRole.id}
                          onClick={() => handleTempSelectCharacter(defaultRole)}
                          className={`p-3 rounded-lg text-left transition-all ${
                            isSelected
                              ? 'bg-blue-600 border-2 border-blue-400 shadow-lg shadow-blue-500/30'
                              : 'bg-gray-800/50 hover:bg-blue-600/30 border border-gray-600/50 hover:border-blue-500/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {defaultRole.icon && <span className="text-xl">{defaultRole.icon}</span>}
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-white'}`}>
                                {i18n.language === "ko" ? defaultRole.name_ko : defaultRole.name_en}
                              </div>
                              {defaultRole.faction && (
                                <div className={`text-xs ${isSelected ? 'text-blue-200' : 'text-gray-400'}`}>
                                  {t(`RoleDistributor.${defaultRole.faction}Faction`)}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })()}
                  </div>
                </div>

                {/* 특수 역할 섹션 */}
                <div>
                  <div className="text-sm font-medium text-purple-300 mb-2 flex items-center">
                    <i className="fas fa-star mr-2" />
                    {t("RoleDistributor.specialRoleSection", "특수 역할")}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {GAME_PRESETS.find(g => g.id === selectedGame)?.characters
                      .filter(char => {
                        const game = GAME_PRESETS.find(g => g.id === selectedGame);
                        return char.id !== game?.defaultRole.id; // 기본 역할 제외
                      })
                      .map(char => {
                        const isSelected = tempSelectedCharacter?.id === char.id;
                        return (
                          <button
                            key={char.id}
                            onClick={() => handleTempSelectCharacter(char)}
                            className={`p-3 rounded-lg text-left transition-all ${
                              isSelected
                                ? 'bg-purple-600 border-2 border-purple-400 shadow-lg shadow-purple-500/30'
                                : 'bg-gray-800/50 hover:bg-purple-600/30 border border-gray-600/50 hover:border-purple-500/50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {char.icon && <span className="text-xl">{char.icon}</span>}
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-white'}`}>
                                  {i18n.language === "ko" ? char.name_ko : char.name_en}
                                </div>
                                {char.faction && (
                                  <div className={`text-xs ${isSelected ? 'text-purple-200' : 'text-gray-400'}`}>
                                    {t(`RoleDistributor.${char.faction}Faction`)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* 선택된 캐릭터 상세 정보 */}
                {tempSelectedCharacter && (
                  <div className="mt-4 p-3 bg-indigo-900/30 border border-indigo-700/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {tempSelectedCharacter.icon && (
                        <span className="text-2xl">{tempSelectedCharacter.icon}</span>
                      )}
                      <div>
                        <div className="font-bold text-white">
                          {i18n.language === "ko" ? tempSelectedCharacter.name_ko : tempSelectedCharacter.name_en}
                        </div>
                        {tempSelectedCharacter.faction && (
                          <div className="text-xs text-indigo-300">
                            {t(`RoleDistributor.${tempSelectedCharacter.faction}Faction`)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 설명 */}
                    <div className="text-sm text-gray-300 mb-3">
                      {i18n.language === "ko"
                        ? tempSelectedCharacter.description_ko
                        : tempSelectedCharacter.description_en}
                    </div>

                    {/* 특수 능력 표시 */}
                    <div className="border-t border-indigo-700/30 pt-2">
                      <div className="text-xs font-medium text-indigo-300 mb-2">
                        {t("RoleDistributor.specialAbilities")}:
                      </div>
                      {tempSelectedCharacter.defaultAbilities.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {tempSelectedCharacter.defaultAbilities.map(abilityId => {
                            const action = coreActions.find(a => a.id === abilityId);
                            return action ? (
                              <span
                                key={abilityId}
                                className="text-xs bg-purple-600/40 px-2 py-1 rounded flex items-center gap-1 text-purple-100"
                              >
                                {action.icon} {action.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 italic">
                          {t("RoleDistributor.noSpecialAbilities")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 모달 푸터 - 버튼 영역 */}
              <div className="px-4 py-3 border-t border-indigo-700/30 flex gap-2">
                <button
                  onClick={handleCancelPreset}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t("RoleDistributor.cancel")}
                </button>
                <button
                  onClick={handleConfirmPreset}
                  disabled={!tempSelectedCharacter}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    tempSelectedCharacter
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {t("RoleDistributor.select")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };


  // 새 역할용 액션 선택기 렌더링
  const renderNewRoleAbilitySelector = () => {
    const coreActions = getCoreActions(t);
    return (
    <div className="mt-2 p-2 bg-gray-800/30 border border-gray-600/30 rounded">
      <div className="text-xs text-gray-300 mb-2">{t("RoleDistributor.selectActions", "액션 선택:")}:</div>
      <div className="grid grid-cols-2 gap-1">
        {coreActions.map((action) => {
          const isSelected = selectedAbilities.includes(action.id);
          return (
            <label
              key={action.id}
              className={`flex items-center gap-1 p-1 rounded cursor-pointer text-xs min-h-[24px] flex-shrink-0 transition-colors duration-200 ${
                isSelected
                  ? 'bg-purple-600/30 border border-purple-500/50'
                  : 'bg-gray-700/30 border border-gray-600/30 hover:bg-gray-600/30'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleNewRoleAbilityToggle(action.id)}
                className="w-3 h-3"
              />
              <span className="ability-icon text-xs">{action.icon}</span>
              <span className="text-xs truncate">{action.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
  };

  // 기본 역할용 액션 선택기 렌더링
  const renderDefaultAbilitySelector = (abilities: string[] = []) => {
    const coreActions = getCoreActions(t);
    return (
    <div className="mt-2 p-2 bg-gray-800/30 border border-gray-600/30 rounded">
      <div className="text-xs text-gray-300 mb-2">{t("RoleDistributor.selectActions", "액션 선택:")}:</div>
      <div className="grid grid-cols-2 gap-1">
        {coreActions.map((action) => {
          const isSelected = abilities.includes(action.id);
          return (
            <label
              key={action.id}
              className={`flex items-center gap-1 p-1 rounded cursor-pointer text-xs min-h-[24px] flex-shrink-0 transition-colors duration-200 ${
                isSelected
                  ? 'bg-blue-600/30 border border-blue-500/50'
                  : 'bg-gray-700/30 border border-gray-600/30 hover:bg-gray-600/30'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleDefaultAbilityToggle(action.id)}
                className="w-3 h-3"
              />
              <span className="ability-icon text-xs">{action.icon}</span>
              <span className="text-xs truncate">{action.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
  };

  // 완료된 기본 역할 렌더링
  const renderCompletedDefaultLabel = () => {
    const coreActions = getCoreActions(t);
    return (
    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg px-3 py-2 mb-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-blue-300 whitespace-nowrap">
          {t("RoleDistributor.basic")}
        </span>
        <span className="text-white font-medium text-sm">
          {state.defaultLabel?.name || t("RoleDistributor.notSet")}
        </span>
      </div>
      {state.defaultLabel?.abilities && state.defaultLabel.abilities.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {state.defaultLabel.abilities.map(abilityId => {
            const action = coreActions.find(a => a.id === abilityId);
            return action ? (
              <span key={abilityId} className="text-xs bg-blue-600/30 px-1 py-0.5 rounded flex items-center gap-1">
                {action.icon} {action.name}
              </span>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
  };

  // 완료된 특수 역할들 렌더링
  const renderCompletedSpecialLabels = () => {
    const coreActions = getCoreActions(t);
    if (state.labels.length === 0) return null;

    return (
      <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg px-3 py-2">
        <div className="text-sm font-medium text-purple-300 mb-2">
          {t("RoleDistributor.specialRoles")}
        </div>
        <div className="grid gap-1.5">
          {state.labels.map((label) => (
            <div
              key={label.name}
              className="p-1.5 bg-purple-900/20 border border-purple-700/30 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium text-sm">
                  {label.name}
                </span>
                <span className="text-purple-200 text-sm">{label.count}</span>
              </div>
              {label.abilities && label.abilities.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {label.abilities.map(abilityId => {
                    const action = coreActions.find(a => a.id === abilityId);
                    return action ? (
                      <span key={abilityId} className="text-xs bg-purple-600/30 px-1 py-0.5 rounded flex items-center gap-1">
                        {action.icon} {action.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 설정 완료 상태 렌더링
  const renderCompletedSettings = () => (
    <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-700/30 rounded-lg px-3 py-2">
      <div className="text-sm font-medium text-green-300 mb-2">
        {t("RoleDistributor.settingsCompleted")}
      </div>
      {renderCompletedDefaultLabel()}
      {renderCompletedSpecialLabels()}
    </div>
  );

  // 기본 역할 입력 렌더링
  const renderDefaultLabelInput = () => (
    <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-700/30 rounded-lg px-3 py-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-blue-300 whitespace-nowrap">
          {t("RoleDistributor.basic")}
        </label>
        <input
          type="text"
          value={defaultLabel}
          maxLength={MAX_LABEL_LENGTH}
          onChange={handleDefaultLabelChange}
          placeholder={t("RoleDistributor.basicPlaceholder")}
          className="flex-1 min-w-0 px-2 py-1 bg-gray-800/50 rounded text-white placeholder-gray-500/50 border-blue-500 ring-1 ring-blue-500/30 text-sm"
        />
      </div>
      {renderDefaultAbilitySelector(state.defaultLabel?.abilities)}
    </div>
  );

  // 수량 조절 버튼 렌더링
  const renderCountControls = () => (
    <div className="flex items-center bg-gray-800/50 rounded border-purple-500 ring-1 ring-purple-500/30">
      <button
        onClick={() => setLabelCount(Math.max(1, labelCount - 1))}
        className="px-2 py-1 text-purple-300 hover:text-white hover:bg-purple-600/20 rounded-l transition-colors text-sm min-w-[28px] min-h-[28px] flex items-center justify-center flex-shrink-0"
      >
        −
      </button>
      {isEditingCount ? (
        <input
          type="number"
          min={1}
          value={labelCount}
          onChange={(e) =>
            setLabelCount(Math.max(1, Number(e.target.value) || 1))
          }
          onBlur={() => setIsEditingCount(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setIsEditingCount(false);
          }}
          className="w-12 px-1 py-1 text-center bg-transparent text-white text-sm border-0 outline-none"
          autoFocus
        />
      ) : (
        <button
          onClick={() => setIsEditingCount(true)}
          className="w-12 px-1 py-1 text-center text-white hover:bg-purple-600/20 transition-colors text-sm min-h-[28px] flex items-center justify-center flex-shrink-0"
        >
          {labelCount}
        </button>
      )}
      <button
        onClick={() => setLabelCount(labelCount + 1)}
        className="px-2 py-1 text-purple-300 hover:text-white hover:bg-purple-600/20 rounded-r transition-colors text-sm min-w-[28px] min-h-[28px] flex items-center justify-center flex-shrink-0"
      >
        +
      </button>
    </div>
  );

  // 특수 역할 추가 영역 렌더링
  const renderSpecialLabelInput = () => (
    <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-700/30 rounded-lg px-3 py-2">
      <div className="flex flex-col gap-3">
        {/* 입력 필드 */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-purple-300 whitespace-nowrap">
            {t("RoleDistributor.special")}
          </span>
          <input
            type="text"
            placeholder={t("RoleDistributor.specialPlaceholder")}
            value={labelName}
            maxLength={MAX_LABEL_LENGTH}
            onChange={(e) => {
              setLabelName(e.target.value);
              // 사용자가 특수 역할을 입력하면 에러 클리어
              if (currentError && e.target.value.trim()) {
                setErrorMessage("");
              }
            }}
            className="flex-1 min-w-0 px-2 py-1 bg-gray-800/50 rounded text-white placeholder-gray-500/50 border-purple-500 ring-1 ring-purple-500/30 text-sm"
          />
        </div>

        {/* 수량 조절과 추가 버튼 */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-2">
            <div className="text-purple-300 text-sm font-medium whitespace-nowrap">
              {t("RoleDistributor.quantity")}
            </div>
            {renderCountControls()}
          </div>
          <button
            onClick={handleAddLabel}
            className="flex-1 px-3 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2 min-h-[36px] flex-shrink-0"
            disabled={!labelName.trim()}
          >
            <i className="fas fa-plus-circle text-sm" />
            <span>{t("RoleDistributor.addSpecialRole")}</span>
          </button>
        </div>

        {/* 액션 선택 영역 */}
        {renderNewRoleAbilitySelector()}
      </div>
    </div>
  );

  // 개별 역할 아이템 렌더링
  const renderLabelItem = (label: { name: string; count: number; abilities?: string[] }) => {
    const coreActions = getCoreActions(t);
    return (
    <div
      key={label.name}
      className="p-2 bg-emerald-900/20 border border-emerald-700/30 rounded-lg"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center ml-2 gap-4 flex-1 min-w-0">
          <div className="flex items-center gap-1 text-blue-300 flex-shrink-0">
            <i className="fas fa-users text-xs" />
            <span className="text-sm font-medium">{label.count}</span>
          </div>
          <span className="text-white font-medium text-sm truncate">
            {label.name}
          </span>
        </div>
        <button
          onClick={() => handleRemoveLabel(label.name)}
          className="px-2 py-1 bg-red-600/80 hover:bg-red-600 text-white rounded text-xs transition-colors flex items-center flex-shrink-0 min-h-[28px]"
        >
          <i className="fas fa-trash mr-1 text-xs" />
          {t("RoleDistributor.delete")}
        </button>
      </div>
      {/* 액션 표시만 (편집 불가) */}
      {label.abilities && label.abilities.length > 0 && (
        <div className="mt-2 p-2 bg-gray-800/30 border border-gray-600/30 rounded">
          <div className="text-xs text-gray-300 mb-2">{t("RoleDistributor.assignedActions", "배정된 액션:")}:</div>
          <div className="flex flex-wrap gap-1">
            {label.abilities.map(abilityId => {
              const action = coreActions.find(a => a.id === abilityId);
              return action ? (
                <span key={abilityId} className="text-xs bg-purple-600/30 px-2 py-1 rounded flex items-center gap-1">
                  {action.icon} {action.name}
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
  };

  // 역할 목록 렌더링
  const renderLabelsList = () => {
    if (state.labels.length === 0) return null;

    return (
      <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-700/30 rounded-lg p-4">
        <h4 className="text-sm font-medium text-emerald-300 mb-4 flex items-center">
          <i className="fas fa-list mr-2" />
          {t("RoleDistributor.addedSpecialRoles")}
        </h4>
        <div className="grid gap-1.5">{state.labels.map(renderLabelItem)}</div>
      </div>
    );
  };

  // 사회자 모드 토글 렌더링
  const renderHostRevealToggle = () => (
    <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border border-amber-700/30 rounded-lg px-3 py-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={state.hostReveal}
          onChange={handleHostRevealChange}
          className="w-4 h-4 mr-2 text-amber-600 bg-gray-800 border-gray-600 rounded focus:ring-amber-500 focus:ring-2 flex-shrink-0"
        />
        <div className="min-w-0">
          <div className="text-white font-medium text-sm">
            {t("RoleDistributor.hostMode")}
          </div>
          <div className="text-gray-400 text-xs leading-tight">
            {t("RoleDistributor.hostModeDescription")}
          </div>
        </div>
      </label>
    </div>
  );

  // 설정 입력 영역 렌더링
  const renderSettingsInput = () => (
    <>
      {renderPresetSelector()}
      {renderDefaultLabelInput()}
      {renderSpecialLabelInput()}
      {renderLabelsList()}
      {renderHostRevealToggle()}
    </>
  );

  // 에러 메시지 렌더링
  const renderErrorMessage = () => {
    if (!currentError) return null;

    return (
      <div className="flex items-center justify-center px-3 py-2 bg-red-900/30 border border-red-700/30 rounded-lg text-red-300 text-sm">
        <i className="fas fa-exclamation-triangle mr-2 text-xs" />
        {currentError}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="space-y-2">
      {renderErrorMessage()}
      {state.settingsCompleted
        ? renderCompletedSettings()
        : renderSettingsInput()}
    </div>
  );
};

export default RoleDistributorInGameSetting;
